import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';

import { middleBreakpoint } from 'constants/onboarding/defaultOnboardingData';
import { CHAIR_EXERCISE_PREVIEW, CHAIR_EXERCISE_VIDEO_LINK } from 'constants/general/links';

import { trackClick } from 'services/Analytics/trackers';

import { getWindowSize } from 'helpers/styles/stylesUtils';

import commonStyles from 'containers/onboarding/CommonStyles.module.css';

import { Box, Button, Header, Image, Text } from 'libComponents';
import VideoWithPreview from 'components/VideoWithPreview';
import SocialTitle from './subComponents/SocialTitle';

import styles from './ObSocialProof.module.css';

import socialOver from 'assets/img/socialProof/socialOver.webp';
import bigSocial from 'assets/img/socialProof/default.webp';
import social from 'assets/img/socialProof/18-25.webp';
import logo from 'assets/img/fullLogo.webp';

import { IObSocialProofContainer } from './types';

const cx = classNames.bind({ ...styles, ...commonStyles });

const ObSocialProofContainer = ({
    toNextPage,
    isOverFlow,
    img,
    users,
    withoutMaxWidth,
    videoLink,
    previewImage,
    titleStyle,
}: IObSocialProofContainer) => {
    const { t } = useTranslation();
    const isMiddleBreakpoint = getWindowSize(middleBreakpoint);

    const withRunningTitle = titleStyle === 'running';
    const defaultImage = isMiddleBreakpoint ? bigSocial : social;
    const image = isOverFlow ? socialOver : defaultImage;

    const [videoLoaded, setVideoLoaded] = useState(false);
    const [firstRun, setFirstRun] = useState(true);

    const handleVideoLoad = () => {
        setVideoLoaded(true);
    };

    const videoClasses = cx('video', {
        visibleVideo: videoLoaded,
        withoutTopBorderRadius: withRunningTitle,
    });

    const previewClasses = cx('previewImage', {
        withoutTopBorderRadius: withRunningTitle,
    });

    const runningTextWrapperClasses = cx('runningTextWrapper', {
        withFirstRun: firstRun,
    });

    const fixedButtonBoxClasses = cx('fixedButtonBox', {
        fixedButtonWithVideo: videoLink,
    });

    const onLogoClick = () => {
        trackClick('ob_social_proof_logo');
    };

    useEffect(() => {
        setTimeout(() => {
            setFirstRun(false);
        }, 5000);
    }, []);

    return (
        <>
            <Header
                left={<Image src={logo} alt="logo" maxWidth={100} onClick={onLogoClick} isForceVisible />}
                backgroundColor="surface-default"
            />
            <Box
                paddingTop={24}
                paddingX={isMiddleBreakpoint ? 0 : 16}
                paddingBottom={isMiddleBreakpoint ? 24 : 128}
                className={styles.container}
            >
                {!withRunningTitle && (
                    <>
                        <Text
                            type={isMiddleBreakpoint ? 'h3' : 'h4'}
                            text={t('onboardingFlow.obSocialProofConf.title', { users: users ?? '65' })}
                            center
                            mb={8}
                            color="on-primary"
                        />
                        <Text
                            type="large-text"
                            text={t('users have chosen us')}
                            center
                            color="text-secondary"
                            mb={16}
                        />
                    </>
                )}
                {Boolean(videoLink) ? (
                    <div className={styles.videoWrapper}>
                        {withRunningTitle && (
                            <Box className={styles.runningText} backgroundColor="success-default">
                                <div className={styles.runningTextWrapper}>
                                    <SocialTitle users={users} />
                                </div>
                                <div className={runningTextWrapperClasses}>
                                    <SocialTitle users={users} />
                                </div>
                            </Box>
                        )}
                        <VideoWithPreview
                            handleVideoLoad={handleVideoLoad}
                            videoLink={videoLink ?? CHAIR_EXERCISE_VIDEO_LINK}
                            previewImage={previewImage ?? CHAIR_EXERCISE_PREVIEW}
                            videoLoaded={videoLoaded}
                            videoClasses={videoClasses}
                            previewClasses={previewClasses}
                        />
                    </div>
                ) : (
                    <Image
                        src={img ?? image}
                        alt="social"
                        {...(!withoutMaxWidth && { maxWidth: 340 })}
                        center
                        isForceVisible
                    />
                )}
                <div className={fixedButtonBoxClasses}>
                    <Button text="Continue" onClick={toNextPage} dataLocator="obNextStep" />
                </div>
            </Box>
        </>
    );
};

export default ObSocialProofContainer;
